import * as THREE from 'three';

export class FikoBatchedMeshManager {
    private batches: Map<string, BatchInfo> = new Map();
    private meshToPartMap: Map<THREE.Mesh, PartInfo> = new Map();
    private batchInstanceMap: Map<THREE.BatchedMesh, Map<number, THREE.Mesh>> = new Map();
    private originalMeshCount: number = 0;
    private batchedMeshCount: number = 0;

    batchAssembly(group: THREE.Group, scene: THREE.Scene, minMeshCount: number = 10): void {
        const startTime = performance.now();
        const meshes: THREE.Mesh[] = [];
        group.traverse((obj) => {
            if (obj instanceof THREE.Mesh && obj.geometry && obj.material) {
                if (obj instanceof THREE.LineSegments || obj instanceof THREE.Line) return;
                meshes.push(obj);
            }
        });

        this.originalMeshCount = meshes.length;
        if (meshes.length < minMeshCount) return;

        const materialGroups = this.groupMeshesByMaterial(meshes);
        materialGroups.forEach((groupMeshes, materialKey) => {
            const batchInfo = this.createBatchedMesh(groupMeshes, materialKey, scene);
            if (batchInfo) {
                this.batches.set(materialKey, batchInfo);
                this.batchedMeshCount++;
            }
        });

        console.log(`[FikoBatchedMeshManager] ${this.originalMeshCount} meshes → ${this.batchedMeshCount} batches in ${(performance.now() - startTime).toFixed(2)}ms`);
    }

    private groupMeshesByMaterial(meshes: THREE.Mesh[]): Map<string, THREE.Mesh[]> {
        const groups = new Map<string, THREE.Mesh[]>();
        for (const mesh of meshes) {
            const key = this.getMaterialKey(mesh.material);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(mesh);
        }
        return groups;
    }

    private getMaterialKey(material: THREE.Material | THREE.Material[]): string {
        if (Array.isArray(material)) return material.map(m => this.getMaterialKey(m)).join('|');
        const mat = material as any;
        const parts: string[] = [mat.type || 'Material'];
        if (mat.color) parts.push(`c${mat.color.getHexString()}`);
        if (mat.metalness !== undefined) parts.push(`m${mat.metalness.toFixed(2)}`);
        if (mat.roughness !== undefined) parts.push(`r${mat.roughness.toFixed(2)}`);
        if (mat.opacity !== undefined && mat.opacity < 1) parts.push(`o${mat.opacity.toFixed(2)}`);
        if (mat.side !== undefined) parts.push(`s${mat.side}`);
        return parts.join('_');
    }

    private createBatchedMesh(meshes: THREE.Mesh[], materialKey: string, scene: THREE.Scene): BatchInfo | null {
        if (meshes.length === 0) return null;

        let maxVertexCount = 0;
        let maxIndexCount = 0;
        const uniqueGeometries = new Map<string, THREE.BufferGeometry>();

        for (const mesh of meshes) {
            const geom = mesh.geometry as THREE.BufferGeometry;
            if (!geom || !geom.attributes.position) continue;
            if (!uniqueGeometries.has(geom.uuid)) {
                uniqueGeometries.set(geom.uuid, geom);
                maxVertexCount += geom.attributes.position.count;
                maxIndexCount += geom.index ? geom.index.count : geom.attributes.position.count;
            }
        }

        const baseMaterial = (meshes[0].material as THREE.Material).clone();

        try {
            const batchedMesh = new THREE.BatchedMesh(
                uniqueGeometries.size, maxVertexCount, maxIndexCount, baseMaterial
            );
            batchedMesh.name = `BatchedMesh_${materialKey}`;
            batchedMesh.frustumCulled = true;
            batchedMesh.sortObjects = false;

            const batchInfo: BatchInfo = {
                batchedMesh, material: baseMaterial,
                geometryIds: [], instanceIds: [], meshKey: materialKey
            };

            const geometryIdMap = new Map<string, number>();
            for (const [geomUuid, geometry] of uniqueGeometries) {
                try {
                    const geometryId = batchedMesh.addGeometry(geometry);
                    geometryIdMap.set(geomUuid, geometryId);
                    batchInfo.geometryIds.push(geometryId);
                } catch (err) { console.error('[BatchedMesh] addGeometry failed:', err); }
            }

            const instanceMap = new Map<number, THREE.Mesh>();
            for (const mesh of meshes) {
                const geometryId = geometryIdMap.get(mesh.geometry.uuid);
                if (geometryId === undefined) continue;
                try {
                    const instanceId = batchedMesh.addInstance(geometryId);
                    batchInfo.instanceIds.push(instanceId);
                    mesh.updateMatrixWorld(true);
                    batchedMesh.setMatrixAt(instanceId, mesh.matrixWorld);
                    instanceMap.set(instanceId, mesh);
                    this.meshToPartMap.set(mesh, {
                        originalMesh: mesh, batchKey: materialKey,
                        geometryId, instanceId
                    });
                    mesh.visible = false;
                } catch (err) { console.error('[BatchedMesh] addInstance failed:', err); }
            }

            this.batchInstanceMap.set(batchedMesh, instanceMap);
            scene.add(batchedMesh);
            return batchInfo;
        } catch (err) {
            console.error('[BatchedMesh] Create failed:', err);
            return null;
        }
    }

    getPartFromInstance(batchedMesh: THREE.BatchedMesh, instanceId: number): THREE.Mesh | null {
        return this.batchInstanceMap.get(batchedMesh)?.get(instanceId) || null;
    }

    setPartVisible(mesh: THREE.Mesh, visible: boolean): void {
        const partInfo = this.meshToPartMap.get(mesh);
        if (!partInfo) return;
        const batchInfo = this.batches.get(partInfo.batchKey);
        if (batchInfo) {
            try { batchInfo.batchedMesh.setVisibleAt(partInfo.instanceId, visible); } catch (err) {}
        }
    }

    dispose(): void {
        this.batches.forEach((info) => {
            if (info.batchedMesh.parent) info.batchedMesh.parent.remove(info.batchedMesh);
            info.batchedMesh.dispose();
            info.material.dispose();
        });
        this.meshToPartMap.forEach((_, mesh) => { mesh.visible = true; });
        this.batches.clear();
        this.meshToPartMap.clear();
        this.batchInstanceMap.clear();
    }

    getStats(): { batchCount: number; instanceCount: number; drawCallReduction: number } {
        let instanceCount = 0;
        this.batches.forEach(info => { instanceCount += info.instanceIds.length; });
        const reduction = this.originalMeshCount > 0
            ? ((this.originalMeshCount - this.batchedMeshCount) / this.originalMeshCount) * 100 : 0;
        return { batchCount: this.batchedMeshCount, instanceCount, drawCallReduction: Math.round(reduction) };
    }
}

interface BatchInfo {
    batchedMesh: THREE.BatchedMesh;
    material: THREE.Material;
    geometryIds: number[];
    instanceIds: number[];
    meshKey: string;
}

interface PartInfo {
    originalMesh: THREE.Mesh;
    batchKey: string;
    geometryId: number;
    instanceId: number;
}
